import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { platformAdminAPI } from '../../services/api'

const formatDateTime = (s) => s ? new Date(s).toLocaleString() : '—'

function DetailModal({ interactionId, onClose }) {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await platformAdminAPI.getLlmInteraction(interactionId)
        if (cancelled) return
        setDetail(res.data?.data || res.data)
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [interactionId])

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(35,32,28,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4rem 1rem' }}>
      <div style={{ background: '#FFFFFF', borderRadius: 8, maxWidth: 900, width: '100%', maxHeight: '85vh', overflow: 'auto', padding: '1.5rem', boxShadow: '0 12px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>LLM interaction</h2>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
        {loading && <div className="loading">Loading…</div>}
        {error && <div style={{ color: '#8B3A3A' }}>{error}</div>}
        {detail && (
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '.5rem 1rem', fontSize: '.875rem' }}>
            <strong>Tenant</strong><span>{detail.tenant_name || detail.tenant_id}</span>
            <strong>Task</strong><span>{detail.task_type}</span>
            <strong>Model</strong><span>{detail.model_provider}/{detail.model_id}</span>
            <strong>Created</strong><span>{formatDateTime(detail.created_at)}</span>
            <strong>Latency</strong><span>{detail.latency_ms}ms</span>
            <strong>Tokens</strong><span>{detail.input_tokens} in / {detail.output_tokens} out</span>
            <strong>Cost</strong><span>${Number(detail.cost_usd || 0).toFixed(6)}</span>
            <strong>Success</strong><span>{detail.success ? '✓' : `✗ ${detail.error_message || ''}`}</span>
            {detail.shipment_id && <><strong>Shipment</strong><span style={{ fontFamily: 'monospace' }}>{detail.shipment_id}</span></>}
            <strong style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>System prompt</strong>
            <pre style={{ gridColumn: '1 / -1', whiteSpace: 'pre-wrap', background: '#F3F2ED', padding: '1rem', borderRadius: 6, fontSize: 12 }}>
              {detail.system_prompt || '—'}
            </pre>
            <strong style={{ gridColumn: '1 / -1' }}>User prompt</strong>
            <pre style={{ gridColumn: '1 / -1', whiteSpace: 'pre-wrap', background: '#F3F2ED', padding: '1rem', borderRadius: 6, fontSize: 12 }}>
              {detail.user_prompt || '—'}
            </pre>
            <strong style={{ gridColumn: '1 / -1' }}>Response</strong>
            <pre style={{ gridColumn: '1 / -1', whiteSpace: 'pre-wrap', background: '#F3F2ED', padding: '1rem', borderRadius: 6, fontSize: 12 }}>
              {detail.response_text || '—'}
            </pre>
            {detail.response_parsed && (
              <>
                <strong style={{ gridColumn: '1 / -1' }}>Parsed JSON</strong>
                <pre style={{ gridColumn: '1 / -1', whiteSpace: 'pre-wrap', background: '#F3F2ED', padding: '1rem', borderRadius: 6, fontSize: 12 }}>
                  {JSON.stringify(detail.response_parsed, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default function AIObservability() {
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({ total: 0, limit: 50, offset: 0 })
  const [filters, setFilters] = useState({ taskType: '', success: '' })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openId, setOpenId] = useState(null)

  const load = useCallback(async (offsetOverride) => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        limit: meta.limit,
        offset: offsetOverride ?? meta.offset,
        ...(filters.taskType && { taskType: filters.taskType }),
        ...(filters.success && { success: filters.success })
      }
      const res = await platformAdminAPI.listLlmInteractions(params)
      setRows(res.data?.data || [])
      setMeta(prev => ({ ...prev, ...(res.data?.meta || {}) }))
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [filters, meta.limit, meta.offset])

  useEffect(() => { load(0) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>AI Observability</h1>
        <Link to="/admin" className="btn btn-secondary">Back to Admin</Link>
      </div>

      <p style={{ marginBottom: '1rem', color: '#23201C', opacity: 0.7 }}>
        Audit trail of every LLM call made by the chat extraction layer, email parser, follow-up generator, and other AI tasks.
        Click a row to see the full prompt + response. Use this to diagnose extraction errors (e.g. when the chat misidentifies a port code).
      </p>

      {error && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', borderRadius: 6, background: 'rgba(139,58,58,0.1)', border: '1px solid #8B3A3A', color: '#8B3A3A', fontSize: '.875rem' }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select value={filters.taskType} onChange={e => setFilters(f => ({ ...f, taskType: e.target.value }))} style={{ padding: '.5rem' }}>
          <option value="">All tasks</option>
          <option value="shipment_intake_chat">shipment_intake_chat</option>
          <option value="quote_extraction">quote_extraction</option>
          <option value="follow_up_email">follow_up_email</option>
        </select>
        <select value={filters.success} onChange={e => setFilters(f => ({ ...f, success: e.target.value }))} style={{ padding: '.5rem' }}>
          <option value="">All results</option>
          <option value="true">Successes only</option>
          <option value="false">Failures only</option>
        </select>
        <button className="btn btn-primary" onClick={() => load(0)}>Apply</button>
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <>
          <div style={{ marginBottom: '.5rem', fontSize: '.875rem', opacity: .7 }}>
            Showing {rows.length} of {meta.total} interactions
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.875rem' }}>
            <thead>
              <tr style={{ background: '#23201C', color: '#FFFFFF' }}>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>When</th>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>Tenant</th>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>Task</th>
                <th style={{ padding: '.5rem', textAlign: 'left' }}>Model</th>
                <th style={{ padding: '.5rem', textAlign: 'right' }}>Tokens</th>
                <th style={{ padding: '.5rem', textAlign: 'right' }}>Latency</th>
                <th style={{ padding: '.5rem', textAlign: 'right' }}>Cost</th>
                <th style={{ padding: '.5rem' }}>OK</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="8" style={{ padding: '1rem', textAlign: 'center', opacity: .7 }}>No interactions match.</td></tr>
              )}
              {rows.map((r, idx) => (
                <tr key={r.id}
                    onClick={() => setOpenId(r.id)}
                    style={{ background: idx % 2 ? '#F3F2ED' : '#FFFFFF', cursor: 'pointer' }}>
                  <td style={{ padding: '.5rem', whiteSpace: 'nowrap' }}>{formatDateTime(r.created_at)}</td>
                  <td style={{ padding: '.5rem' }}>{r.tenant_name || (r.tenant_id || '').slice(0, 8)}</td>
                  <td style={{ padding: '.5rem' }}>{r.task_type}</td>
                  <td style={{ padding: '.5rem' }}>{r.model_provider}/{r.model_id}</td>
                  <td style={{ padding: '.5rem', textAlign: 'right' }}>{r.input_tokens || 0}/{r.output_tokens || 0}</td>
                  <td style={{ padding: '.5rem', textAlign: 'right' }}>{r.latency_ms}ms</td>
                  <td style={{ padding: '.5rem', textAlign: 'right' }}>${Number(r.cost_usd || 0).toFixed(4)}</td>
                  <td style={{ padding: '.5rem', textAlign: 'center', color: r.success ? '#5C7A54' : '#8B3A3A' }}>
                    {r.success ? '✓' : '✗'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {openId && <DetailModal interactionId={openId} onClose={() => setOpenId(null)} />}
    </div>
  )
}
